import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {updateDraftItems} from 'common/src/modules/contentFeeds/store/contentFeedPage/thunks/updateDraftItems.thunk';
import assertAiGeneratedContentFeed, {
  CONTENT_FEED_AI_TOOL_CONTEXT_ERROR,
} from '../utils/assertAiGeneratedContentFeed';
import {ContentFeedAiGeneratedSkillToolName} from '../skills/contentFeedAiGeneratedSkill.enums';
import linkMissingAssetsToContentFeed from '../utils/linkMissingAssetsToContentFeed';
import mergeDraftColumnValues from '../utils/mergeDraftColumnValues';
import validatePersonaColumnValues from '../utils/validatePersonaColumnValues';

const inputSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.number().describe('Draft item ID'),
        name: z.string().optional().nullable().describe('New name for the draft item'),
        columnValues: z
          .record(z.string())
          .optional()
          .nullable()
          .describe(
            'Partial map of internal columnKey (e.g. "Text1", "Image5") to value updates. Use the columnKey from the feed column definitions, NOT the display name. For Persona columns use a numeric persona ID from the brief. Merged with existing column data.',
          ),
      }),
    )
    .describe('Draft items to update'),
});

export default function updateDraftItemsTool(store: CLStore) {
  async function execute({
    input: {items},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed;

    if (!assertAiGeneratedContentFeed(feed)) {
      return toolError(CONTENT_FEED_AI_TOOL_CONTEXT_ERROR);
    }

    if (!items?.length) {
      return toolError('No items provided. Specify at least one draft item to update.');
    }

    const existingItems = state.contentFeedPage?.draftItems?.items ?? [];
    const feedId = feed.id;

    for (const item of items) {
      const existingItem = existingItems.find((e) => e.id === item.id);

      if (!existingItem) {
        return toolError(
          `Draft item ${item.id} not found in the current feed draft list. Load draft items on the Draft Items step first.`,
        );
      }

      const hasName = item.name !== undefined && item.name !== null;
      const hasColumnUpdates = item.columnValues !== undefined && item.columnValues !== null;

      if (!hasName && !hasColumnUpdates) {
        return toolError(
          `Draft item ${item.id}: provide name and/or columnValues to update.`,
        );
      }

      if (hasColumnUpdates) {
        const personaError = validatePersonaColumnValues(feed, item.columnValues);
        
        if (personaError) {
          return toolError(`Draft item ${item.id}: ${personaError}`);
        }
      }
    }

    await linkMissingAssetsToContentFeed(
      store,
      feed,
      items.map((i) => ({columnValues: i.columnValues ?? null})),
    );

    const entries = items.map((item) => {
      const existingItem = existingItems.find((e) => e.id === item.id);
      const hasName = item.name !== undefined && item.name !== null;
      const hasColumnUpdates = item.columnValues !== undefined && item.columnValues !== null;
      const mergedColumnValues = hasColumnUpdates
        ? mergeDraftColumnValues(existingItem?.columnValues ?? undefined, item.columnValues ?? undefined)
        : undefined;

      return {
        feedId,
        itemId: item.id,
        request: {
          ...(hasName && {name: item.name}),
          ...(mergedColumnValues !== undefined && {columnValues: mergedColumnValues}),
        },
      };
    });

    try {
      await store.dispatch(updateDraftItems(entries)).unwrap();

      const ids = items.map((i) => i.id).join(', ');

      return toolResult(`Successfully updated ${items.length} draft item(s) (IDs: ${ids}).`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error updating draft items: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Updating draft items',
    name: ContentFeedAiGeneratedSkillToolName.UpdateDraftItems,
    description:
      'Updates one or more draft items: name and/or column values (input columns and creative column values). Values are merged with existing draft data.',
    execute,
    inputSchema,
  });
}
